import { useMemo } from "react";
import { OtsDateTime } from "../components/OtsDateTime";
import { OtsGoogleMap, type OtsMapMarker, type OtsMapPath, type OtsMapPosition } from "../components/OtsGoogleMap";
import { type ActivityDayStopKind, driverDayStops } from "./model/activity-day-map";
import type { ActivityDriver } from "./driver-activity-view-model";

export type ActivityDayMapProps = {
  driver: ActivityDriver;
  apiKey?: string;
  /** Each shift's recorded road; see `ConnectedActivityDayMap`. */
  paths?: OtsMapPosition[][];
  timeZone?: string;
  /** Opens the row a stop came from. */
  onOpenEntry?: (entryKey: string) => void;
};

const stopColors: Record<ActivityDayStopKind, string> = { pickup: "#16a34a", delivery: "#dc2626", visit: "#7c3aed" };
const stopNames: Record<ActivityDayStopKind, string> = { pickup: "Pickup", delivery: "Delivery", visit: "Site visit" };
const NO_PATHS: OtsMapPosition[][] = [];

/**
 * A driver's whole day on one map: every pickup, delivery, and site visit numbered in the order it happened, along
 * the road the truck recorded, with the same stops listed beside it.
 */
export const ActivityDayMap = ({ driver, apiKey, paths = NO_PATHS, timeZone, onOpenEntry }: ActivityDayMapProps) => {
  const stops = useMemo(() => driverDayStops(driver), [driver]);
  const markers = useMemo<OtsMapMarker[]>(() => stops.map((stop) => ({
    id: stop.id,
    position: stop.position,
    label: `${stop.order}. ${stop.label}`,
    color: stopColors[stop.kind],
    size: stop.kind === "visit" ? 8 : 10,
    text: String(stop.order),
  })), [stops]);
  const roads = useMemo<OtsMapPath[]>(() => paths.map((path, index) => ({ id: `shift-${index}`, path, color: "#38bdf8" })), [paths]);

  return (
    <div className="grid gap-3 @container md:grid-cols-[minmax(0,1fr)_16rem]">
      <OtsGoogleMap
        apiKey={apiKey}
        markers={markers}
        paths={roads}
        emptyMessage={`No locations were recorded for ${driver.name} on this day.`}
        subject={`${driver.name}'s day`}
        className="h-[26rem]"/>
      <div className="min-w-0">
        <div aria-hidden="true" className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--ots-text-muted)]">
          {(Object.keys(stopColors) as ActivityDayStopKind[]).map((kind) => (
            <span key={kind} className="flex items-center gap-1">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: stopColors[kind] }}/>{stopNames[kind]}
            </span>
          ))}
          {roads.length > 0 && <span className="flex items-center gap-1"><span className="h-0.5 w-3 bg-[#38bdf8]"/>Road taken</span>}
        </div>
        <ol aria-label={`${driver.name}'s stops`} className="max-h-[23rem] space-y-1 overflow-auto text-xs">
          {stops.map((stop) => (
            <li key={stop.id}>
              <button
                type="button"
                disabled={!onOpenEntry}
                onClick={() => onOpenEntry?.(stop.entryKey)}
                className="flex w-full items-start gap-2 rounded px-1.5 py-1 text-left enabled:hover:bg-[var(--ots-surface-variant)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)] disabled:cursor-default">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: stopColors[stop.kind] }}>{stop.order}</span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{stop.label}</span>
                  <span className="text-[var(--ots-text-muted)]"><OtsDateTime value={stop.time} format="time" timeZone={timeZone}/></span>
                </span>
              </button>
            </li>
          ))}
          {!stops.length && <li className="text-[var(--ots-text-muted)]">No stops have a location.</li>}
        </ol>
      </div>
    </div>
  );
};
